// 랭킹 가중치·플래그 튜닝 + 이력/롤백 + 피드 큐레이션 — 05-admin §7.1 / §7.2.
//   GET/PATCH /v1/admin/flags, GET /v1/admin/flags/history, POST /v1/admin/flags/rollback,
//   POST /v1/admin/ranking/recompute, GET/PUT /v1/admin/feed/curation (01 §3.M, admin).
import { api, errMsg, isNotReady } from "../core/api.js";
import { h, mount, notReadyState } from "../core/dom.js";
import type { AuditEntry, FeedCuration, Flags, Page } from "../models.js";

// 알려진 랭킹 가중치 키(00 §11-Q8) — 초기값 힌트 표시용.
const KNOWN_WEIGHTS: { key: string; label: string; hint: string }[] = [
  { key: "w_v", label: "w_v (조회)", hint: "1.0" },
  { key: "w_c", label: "w_c (완료율)", hint: "2.0" },
  { key: "w_u", label: "w_u (추천)", hint: "1.5" },
  { key: "w_r", label: "w_r (recency 반감기)", hint: "7d" },
  { key: "w_p", label: "w_p (penalty)", hint: "—" },
  { key: "epsilon", label: "ε (탐험비율, 콜드스타트)", hint: "0.1" },
  { key: "showcase_min_views", label: "노출자격 최소 조회", hint: "—" },
];

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleString("ko-KR");
}

function toStr(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// 입력값을 number/boolean/string으로 보수적 파싱.
function coerce(raw: string): unknown {
  const t = raw.trim();
  if (t === "") return "";
  if (t === "true") return true;
  if (t === "false") return false;
  const n = Number(t);
  return Number.isFinite(n) && t === String(n) ? n : t;
}

// ── 플래그 섹션 ────────────────────────────────────────────
function flagsSection(): HTMLElement {
  const wrap = h("section", { class: "card" }, h("h2", {}, "랭킹 가중치 · 피처플래그 (KV)"));
  const body = h("div", {}, h("div", { class: "state muted" }, "플래그 불러오는 중…"));
  const status = h("p", { class: "small muted" }, "");
  let current: Flags = {};
  const inputs = new Map<string, HTMLInputElement>();

  function renderForm(): void {
    inputs.clear();
    const keys = Array.from(new Set([...KNOWN_WEIGHTS.map((w) => w.key), ...Object.keys(current)]));
    const rows = keys.map((key) => {
      const meta = KNOWN_WEIGHTS.find((w) => w.key === key);
      const field = h("input", {
        class: "field",
        value: toStr(current[key]),
        placeholder: meta?.hint ?? "",
      }) as HTMLInputElement;
      inputs.set(key, field);
      return h(
        "div",
        { class: "row" },
        h("label", { class: "lbl", style: "min-width:220px" }, meta?.label ?? key),
        field,
      );
    });
    body.replaceChildren(
      ...rows,
      h(
        "div",
        { class: "row wrap" },
        h("button", { class: "btn", onclick: () => void save() }, "변경 저장 (PATCH, admin)"),
        h("button", { class: "btn secondary", onclick: () => void recompute() }, "score 재계산 트리거"),
      ),
      status,
    );
  }

  async function load(): Promise<void> {
    try {
      current = await api.get<Flags>("/v1/admin/flags");
      renderForm();
    } catch (e) {
      if (isNotReady(e)) {
        body.replaceChildren(notReadyState("피처플래그 (/v1/admin/flags) 미구현."));
        return;
      }
      body.replaceChildren(h("div", { class: "state error" }, `플래그 불러오기 실패: ${errMsg(e)}`));
    }
  }

  async function save(): Promise<void> {
    const patch: Flags = {};
    for (const [key, field] of inputs) {
      const v = coerce(field.value);
      if (v !== "") patch[key] = v;
    }
    status.textContent = "저장 중…";
    status.className = "small muted";
    try {
      await api.patch("/v1/admin/flags", patch);
      status.textContent = "플래그 변경 저장됨 (감사 로그 기록).";
      status.className = "small ok";
      void load();
    } catch (e) {
      status.textContent = isNotReady(e) ? "엔드포인트 준비중 (PATCH 미구현)." : `저장 실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  async function recompute(): Promise<void> {
    if (!window.confirm("전체 score 재계산 잡을 트리거할까요? (§6.2)")) return;
    status.textContent = "재계산 트리거 중…";
    status.className = "small muted";
    try {
      await api.post("/v1/admin/ranking/recompute", {});
      status.textContent = "재계산 잡이 큐에 등록되었습니다.";
      status.className = "small ok";
    } catch (e) {
      status.textContent = isNotReady(e) ? "엔드포인트 준비중 (recompute 미구현)." : `실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  wrap.appendChild(body);
  void load();
  return wrap;
}

// ── 변경 이력 / 롤백 섹션 ──────────────────────────────────
function historySection(): HTMLElement {
  const wrap = h("section", { class: "card" }, h("h2", {}, "플래그 변경 이력 / 롤백"));
  const body = h("div", {}, h("div", { class: "state muted" }, "이력 불러오는 중…"));
  wrap.appendChild(body);

  async function rollback(entry: AuditEntry): Promise<void> {
    if (!window.confirm("이 시점의 플래그 스냅샷으로 롤백할까요? (감사 로그 기록)")) return;
    try {
      await api.post("/v1/admin/flags/rollback", { audit_id: entry.id });
      void load();
    } catch (e) {
      window.alert(isNotReady(e) ? "엔드포인트 준비중 (rollback 미구현)." : `롤백 실패: ${errMsg(e)}`);
    }
  }

  async function load(): Promise<void> {
    try {
      const page = await api.get<Page<AuditEntry> | AuditEntry[]>("/v1/admin/flags/history?limit=30");
      const items = Array.isArray(page) ? page : (page.items ?? []);
      if (items.length === 0) {
        body.replaceChildren(h("div", { class: "state muted" }, "변경 이력이 없습니다."));
        return;
      }
      body.replaceChildren(
        h(
          "table",
          { class: "tbl" },
          h("thead", {}, h("tr", {}, h("th", {}, "시각"), h("th", {}, "actor"), h("th", {}, "action"), h("th", {}, ""))),
          h(
            "tbody",
            {},
            ...items.map((e) =>
              h(
                "tr",
                {},
                h("td", { class: "muted small" }, fmtTime(e.created_at)),
                h("td", { class: "mono small" }, e.actor_id ?? "—"),
                h("td", {}, h("span", { class: "badge" }, e.action)),
                h("td", {}, h("button", { class: "btn small secondary", onclick: () => void rollback(e) }, "이 시점으로 롤백")),
              ),
            ),
          ),
        ),
      );
    } catch (e) {
      if (isNotReady(e)) {
        body.replaceChildren(notReadyState("플래그 이력 (/v1/admin/flags/history) 미구현."));
        return;
      }
      body.replaceChildren(h("div", { class: "state error" }, `이력 불러오기 실패: ${errMsg(e)}`));
    }
  }

  void load();
  return wrap;
}

// ── 피드 큐레이션 섹션 (§7.2) ──────────────────────────────
function feedSection(): HTMLElement {
  const wrap = h("section", { class: "card" }, h("h2", {}, "피드 큐레이션 (수동 핀/제외)"));
  const body = h("div", {}, h("div", { class: "state muted" }, "큐레이션 불러오는 중…"));
  const status = h("p", { class: "small muted" }, "");

  const titleField = h("input", { class: "field", placeholder: "title_id" }) as HTMLInputElement;
  const rankField = h("input", { class: "field", placeholder: "curated_rank (낮을수록 상단, 비우면 핀 해제)" }) as HTMLInputElement;

  async function setPin(): Promise<void> {
    const titleId = titleField.value.trim();
    if (!titleId) {
      status.textContent = "title_id를 입력하세요.";
      status.className = "small error";
      return;
    }
    const rankRaw = rankField.value.trim();
    const curated_rank = rankRaw === "" ? null : Number(rankRaw);
    if (curated_rank !== null && !Number.isFinite(curated_rank)) {
      status.textContent = "curated_rank는 숫자여야 합니다.";
      status.className = "small error";
      return;
    }
    status.textContent = "저장 중…";
    status.className = "small muted";
    try {
      await api.put("/v1/admin/feed/curation", { title_id: titleId, curated_rank });
      status.textContent = curated_rank === null ? "핀 해제됨." : `핀 설정됨 (rank ${curated_rank}).`;
      status.className = "small ok";
      void load();
    } catch (e) {
      status.textContent = isNotReady(e) ? "엔드포인트 준비중 (feed/curation PUT 미구현)." : `저장 실패: ${errMsg(e)}`;
      status.className = "small error";
    }
  }

  async function load(): Promise<void> {
    try {
      const page = await api.get<Page<FeedCuration> | FeedCuration[]>("/v1/admin/feed/curation");
      const items = Array.isArray(page) ? page : (page.items ?? []);
      const list =
        items.length === 0
          ? h("div", { class: "state muted" }, "핀된 타이틀이 없습니다.")
          : h(
              "table",
              { class: "tbl" },
              h("thead", {}, h("tr", {}, h("th", {}, "title"), h("th", {}, "curated_rank"))),
              h(
                "tbody",
                {},
                ...items.map((c) =>
                  h(
                    "tr",
                    {},
                    h("td", {}, c.title ?? h("span", { class: "mono small" }, c.title_id)),
                    h("td", { class: "muted small" }, c.curated_rank != null ? String(c.curated_rank) : "—"),
                  ),
                ),
              ),
            );
      body.replaceChildren(
        h("div", { class: "row wrap" }, titleField, rankField, h("button", { class: "btn", onclick: () => void setPin() }, "핀 설정/해제")),
        status,
        list,
      );
    } catch (e) {
      if (isNotReady(e)) {
        body.replaceChildren(notReadyState("피드 큐레이션 (/v1/admin/feed/curation) 미구현."));
        return;
      }
      body.replaceChildren(h("div", { class: "state error" }, `불러오기 실패: ${errMsg(e)}`));
    }
  }

  wrap.appendChild(body);
  void load();
  return wrap;
}

export function rankingView(_p: Record<string, string>, root: HTMLElement): void {
  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "랭킹 / 피드 큐레이션"),
      h(
        "p",
        { class: "muted" },
        "KV 피처플래그로 코드 배포 없이 런타임 튜닝 (05-admin §7.1). 변경=admin + 감사 로그. 이력/롤백·피드 핀(§7.2) 포함.",
      ),
      flagsSection(),
      historySection(),
      feedSection(),
    ),
  );
}
